import React, { useState } from 'react'
import { Plus, Settings } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Button } from '@/app/components/ui/button'
import { InventoryItem } from '@/app/services/inventory.service'

interface AdjustModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedItem: InventoryItem | null
  onApplyAdjustment: (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
  }) => void
}

export function AdjustModal({
  open,
  onOpenChange,
  selectedItem,
  onApplyAdjustment,
}: AdjustModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add')
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('')
  const [adjustmentReason, setAdjustmentReason] = useState('')

  const handleApply = () => {
    if (selectedItem) {
      onApplyAdjustment({
        item: selectedItem.name,
        type: adjustmentType,
        quantity: adjustmentQuantity,
        reason: adjustmentReason,
      })
    }
    setAdjustmentQuantity('')
    setAdjustmentReason('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ajustar Stock</DialogTitle>
          <DialogDescription className="sr-only">
            Agregar o restar unidades del inventario con motivo
          </DialogDescription>
        </DialogHeader>
        {selectedItem && (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-sm font-medium text-slate-900">
                {selectedItem.name}
              </p>
              <p className="mt-1 text-xs text-slate-600">
                Stock actual: {selectedItem.stock} unidades
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Tipo de ajuste:
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setAdjustmentType('add')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    adjustmentType === 'add'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Plus className="mx-auto mb-1 h-5 w-5" />
                  <p className="text-xs font-medium">Agregar</p>
                </button>
                <button
                  onClick={() => setAdjustmentType('subtract')}
                  className={`rounded-lg border-2 p-3 transition-all ${
                    adjustmentType === 'subtract'
                      ? 'border-red-500 bg-red-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <Settings className="mx-auto mb-1 h-5 w-5" />
                  <p className="text-xs font-medium">Restar</p>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Cantidad:
              </label>
              <Input
                type="number"
                value={adjustmentQuantity}
                onChange={(e) => setAdjustmentQuantity(e.target.value)}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Motivo:
              </label>
              <Input
                type="text"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Ej: Merma, Devolución, Corrección"
              />
            </div>

            {adjustmentQuantity && (
              <div className="rounded-lg bg-blue-50 p-3">
                <p className="text-sm text-blue-900">
                  Nuevo stock:{' '}
                  <strong>
                    {adjustmentType === 'add'
                      ? selectedItem.stock + parseInt(adjustmentQuantity || '0')
                      : selectedItem.stock - parseInt(adjustmentQuantity || '0')}{' '}
                    unidades
                  </strong>
                </p>
              </div>
            )}

            <Button
              onClick={handleApply}
              disabled={!adjustmentQuantity || !adjustmentReason}
              className="w-full"
            >
              Aplicar Ajuste
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
